// Icon — lucide-react wrapper with a closed `IconName` union.
// Port target: legacy `src/ui/Icon.jsx` — inline-SVG dictionary replaced
// with lucide-react icons. The `MAP` is frozen (`as const satisfies ...`)
// so `IconProps.name` becomes a literal union; unknown names fail at
// `tsc --noEmit` instead of rendering a placeholder (see).
import type { CSSProperties } from 'react';
import {
  Package,
  CheckSquare,
  LayoutList,
  PhoneCall,
  Truck,
  Gauge,
  UserPlus,
  Handshake,
  BarChart3,
  Users,
  Building2,
  Banknote,
  Ship,
  Warehouse,
  Tag,
  Radio,
  GitBranch,
  User,
  UsersRound,
  Shield,
  ScrollText,
  Search,
  Bell,
  Sun,
  Moon,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  SendHorizontal,
  RotateCcw,
  CheckCheck,
  Check,
  Trophy,
  Lock,
  Archive,
  ArchiveRestore,
  ImagePlus,
  Paperclip,
  GripVertical,
  Trash2,
  PencilLine,
  Copy,
  ExternalLink,
  Timer,
  TimerOff,
  MessageSquare,
  History,
  Scale,
  Box,
  FileEdit,
  Loader,
  Undo2,
  CheckCircle2,
  X,
  MoreHorizontal,
  Filter,
  Calendar,
  ArrowRight,
  MapPin,
  AlertTriangle,
  Sparkles,
  Star,
  StarHalf,
  Crown,
  MessageCircle,
  PackageOpen,
  Zap,
  BadgeDollarSign,
  Sliders,
  Clock,
  Image as ImageIcon,
  MoreVertical,
  LogOut,
  Settings,
  UserCog,
  FileText,
  Download,
  Upload,
  CircleCheck,
  CircleDot,
  Link as LinkIcon,
  Save,
  Eye,
  EyeOff,
  Mail,
  RefreshCw,
  XCircle,
  ArrowLeft,
  Kanban,
  List,
  Flag,
  Dot,
  Inbox,
  HelpCircle,
  CheckCircle,
  ArrowUpDown,
  Columns,
  Briefcase,
  Cpu,
  Globe,
  Gavel,
  PhoneOutgoing,
  Phone,
  HardHat,
  UploadCloud,
  AlertCircle,
  ClipboardPaste,
  type LucideIcon,
} from 'lucide-react';

// `as const` narrows the keys to a literal union so `IconProps.name`
// is a closed set — TypeScript rejects `<Icon name="nope" />` at
// compile time. Dynamic names require `expr: IconName` — no escape hatch.
const MAP = {
  package: Package,
  'check-square': CheckSquare,
  'layout-list': LayoutList,
  'phone-call': PhoneCall,
  truck: Truck,
  gauge: Gauge,
  'user-plus': UserPlus,
  handshake: Handshake,
  'bar-chart-3': BarChart3,
  users: Users,
  'building-2': Building2,
  banknote: Banknote,
  ship: Ship,
  warehouse: Warehouse,
  tag: Tag,
  radio: Radio,
  'git-branch': GitBranch,
  user: User,
  'users-round': UsersRound,
  shield: Shield,
  'scroll-text': ScrollText,
  search: Search,
  bell: Bell,
  sun: Sun,
  moon: Moon,
  'chevron-down': ChevronDown,
  'chevron-right': ChevronRight,
  'chevron-left': ChevronLeft,
  'panel-left-close': PanelLeftClose,
  'panel-left-open': PanelLeftOpen,
  plus: Plus,
  'send-horizontal': SendHorizontal,
  'rotate-ccw': RotateCcw,
  'check-check': CheckCheck,
  check: Check,
  trophy: Trophy,
  lock: Lock,
  archive: Archive,
  'archive-restore': ArchiveRestore,
  'image-plus': ImagePlus,
  paperclip: Paperclip,
  'grip-vertical': GripVertical,
  'trash-2': Trash2,
  'pencil-line': PencilLine,
  copy: Copy,
  'external-link': ExternalLink,
  timer: Timer,
  'timer-off': TimerOff,
  'message-square': MessageSquare,
  history: History,
  scale: Scale,
  box: Box,
  'file-edit': FileEdit,
  loader: Loader,
  'undo-2': Undo2,
  'check-circle-2': CheckCircle2,
  x: X,
  'more-horizontal': MoreHorizontal,
  filter: Filter,
  calendar: Calendar,
  'arrow-right': ArrowRight,
  'map-pin': MapPin,
  'alert-triangle': AlertTriangle,
  sparkles: Sparkles,
  star: Star,
  'star-half': StarHalf,
  crown: Crown,
  'message-circle': MessageCircle,
  'package-open': PackageOpen,
  zap: Zap,
  'badge-dollar-sign': BadgeDollarSign,
  sliders: Sliders,
  clock: Clock,
  image: ImageIcon,
  'more-vertical': MoreVertical,
  'log-out': LogOut,
  settings: Settings,
  'user-cog': UserCog,
  'file-text': FileText,
  download: Download,
  upload: Upload,
  'circle-check': CircleCheck,
  'circle-dot': CircleDot,
  link: LinkIcon,
  save: Save,
  eye: Eye,
  'eye-off': EyeOff,
  mail: Mail,
  'refresh-cw': RefreshCw,
  'x-circle': XCircle,
  'arrow-left': ArrowLeft,
  kanban: Kanban,
  list: List,
  flag: Flag,
  'users-group': Users, // legacy alias — same glyph as 'users'
  dot: Dot,
  inbox: Inbox,
  'help-circle': HelpCircle,
  'check-circle': CheckCircle,
  'arrow-up-down': ArrowUpDown,
  funnel: Filter, // legacy alias — same polygon glyph as 'filter'
  columns: Columns,
  briefcase: Briefcase,
  cpu: Cpu,
  globe: Globe,
  gavel: Gavel,
  'phone-outgoing': PhoneOutgoing,
  phone: Phone,
  'hard-hat': HardHat,
  'upload-cloud': UploadCloud,
  'alert-circle': AlertCircle,
  'clipboard-paste': ClipboardPaste,
} as const satisfies Record<string, LucideIcon>;

export type IconName = keyof typeof MAP;

export interface IconProps {
  name: IconName;
  size?: number;
  className?: string;
  style?: CSSProperties;
}

export function Icon({ name, size = 16, className, style }: IconProps) {
  const Cmp = MAP[name];
  return (
    <Cmp
      width={size}
      height={size}
      strokeWidth={1.5}
      className={className}
      style={{ flex: 'none', ...style }}
    />
  );
}
